import * as React from "react";
import { Link } from "react-router-dom";

import { Svg } from "@/assets";
import { Avatar, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";
import { extractInitials, URLS } from "@/utils";

import { coachKanbanStatusLabel } from "./coachKanbanStatusLabel";

interface CoachVideoProjectKanbanCardProps {
  videoProject: VideoProject;
}

export const CoachVideoProjectKanbanCard = React.memo(function CoachVideoProjectKanbanCard({
  videoProject,
}: CoachVideoProjectKanbanCardProps) {
  const statusLabel = coachKanbanStatusLabel(videoProject);
  const playerName = videoProject.playerName || "Untitled Player";

  return (
    <Card
      size="sm"
      className={cn(
        "relative gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10 transition-[box-shadow,ring-color,ring-width] duration-200 hover:shadow-md hover:ring-foreground/20",
        videoProject.needsAck && "ring-amber-500"
      )}
    >
      <Link
        to={URLS.coach.videoProject.replace(":id", `${videoProject.id}`)}
        aria-label={playerName}
        className="absolute inset-0 z-0"
      />
      <div className="relative z-10 flex flex-col gap-2 p-2.5 pointer-events-none">
        <div className="flex items-start gap-2.5">
          <div className="bg-muted size-16 shrink-0 overflow-hidden rounded-md">
            <img
              src={videoProject.thumbnailUrl || Svg.PromptPlaceholder}
              alt=""
              className={cn(
                "size-full",
                videoProject.thumbnailUrl ? "object-cover object-center" : "object-none object-center"
              )}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
            <div className="flex items-start gap-1.5">
              <Avatar size="sm" title={playerName} className="mt-0.5">
                <Avatar.Image src={videoProject.playerPhotoUrl || undefined} alt="" />
                <Avatar.Fallback className="text-[8px]">{extractInitials(playerName)}</Avatar.Fallback>
              </Avatar>
              <div className="text-foreground line-clamp-2 min-w-0 flex-1 text-left text-sm leading-snug font-medium tracking-tight">
                {playerName}
              </div>
            </div>
            {statusLabel ? (
              <div className="text-muted-foreground line-clamp-2 text-[11px] leading-snug" title={statusLabel}>
                {statusLabel}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
});
